import { useConfirm, useNotify } from "components/admin/Feedback";
import { formatNumber } from "utils/format";

/**
 * Hỏi lại trước khi lưu một form sửa: liệt kê "• Nhãn: cũ → mới" cho từng ô đã đổi, cảnh báo khi
 * một con số đổi từ 2 lần trở lên (dễ gõ thừa / thiếu số 0), và không mở hộp nếu chưa đổi gì.
 *
 * fields: [key, label] hoặc [key, label, { text: (v) => chuỗi hiển thị }] — mặc định số thì
 * định dạng 1.000, chữ thì để nguyên, mảng / object (quà...) chỉ ghi "• Nhãn" khi khác.
 */

const isNum = (v) => typeof v === "number" || (typeof v === "string" && /^-?\d+(\.\d+)?$/.test(v));
const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

function show(v, opts) {
  if (opts?.text) return opts.text(v);
  if (isNum(v)) return formatNumber(Number(v));
  if (typeof v === "boolean") return v ? "bật" : "tắt";
  return v === null || v === undefined || v === "" ? "(trống)" : `"${v}"`;
}

export function diffLines(before, after, fields) {
  const lines = [];
  const jumps = [];
  fields.forEach(([key, label, opts]) => {
    const a = before?.[key];
    const b = after?.[key];
    if (isNum(a) && isNum(b)) {
      if (Number(a) === Number(b)) return;
      const from = Number(a);
      const to = Number(b);
      const pct = from !== 0 ? Math.round(((to - from) * 100) / Math.abs(from)) : null;
      lines.push(
        `• ${label}: ${show(a, opts)} → ${show(b, opts)}${
          pct !== null ? ` (${pct > 0 ? "+" : ""}${pct}%)` : ""
        }`
      );
      if (from > 0 && (to >= from * 2 || to * 2 <= from)) jumps.push(label);
      return;
    }
    if (same(a, b)) return;
    if (typeof a === "object" || typeof b === "object") lines.push(`• ${label}`);
    else lines.push(`• ${label}: ${show(a, opts)} → ${show(b, opts)}`);
  });
  return { lines, jumps };
}

/** Trả về hàm async ({ title, before, after, fields, notes, confirmText }) => true nếu người dùng đồng ý. */
export default function useConfirmChanges() {
  const confirm = useConfirm();
  const notify = useNotify();
  return async ({
    title,
    before,
    after,
    fields,
    notes = [],
    confirmText = "Lưu",
    isNew = false,
  }) => {
    const { lines, jumps } = isNew ? { lines: [], jumps: [] } : diffLines(before, after, fields);
    if (!isNew && lines.length === 0) {
      notify.info("Chưa có gì thay đổi");
      return false;
    }
    return confirm({
      title,
      message: [
        ...lines,
        lines.length ? "" : null,
        jumps.length
          ? `⚠ ${jumps.join(
              ", "
            )} thay đổi từ 2 lần trở lên — kiểm tra lại có gõ thừa / thiếu số 0 không.`
          : null,
        ...notes.filter(Boolean),
      ]
        .filter((l) => l !== null)
        .join("\n"),
      color: jumps.length ? "error" : "warning",
      confirmText,
    });
  };
}
